import React, {FC, useEffect, useState} from 'react'
import type {PartDto, PartStorageLocation, PartStorageSubLocation, UomDto} from '../../core/_models'
import {getPartStorageLocations, getUnitsOfMeasure} from '../../core/_requests'
import SearchableSelect from '../../../../shared/components/SearchableSelect'

interface PartModalProps {
  visible: boolean
  subcomponentId: number
  editing?: PartDto
  onClose: () => void
  onSubmit: (data: PartDto) => void
  context?: {
    companyName?: string
    vesselName?: string
    equipmentName?: string
    subcomponentName?: string
    vesselId?: number
    equipmentId?: number
  }
}

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'
const normalizeUom = (value?: string | null) => (value ?? '').trim().toLowerCase()
const buildUomOptions = (items: UomDto[]) => (Array.isArray(items) ? items : [])
const resolveUomId = (value: string | undefined, options: UomDto[]) => {
  if (!value) return ''
  const match = options.find(
    (uom) =>
      normalizeUom(uom.code) === normalizeUom(value) ||
      normalizeUom(uom.name) === normalizeUom(value)
  )
  return match ? String(match.id) : ''
}
const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

type PartFormState = Omit<PartDto, 'robQuantity' | 'minThreshold' | 'maxThreshold' | 'uom' | 'unitOfMeasure' | 'uomId'> & {
  uom?: UomDto
  uomId?: string
  unitOfMeasure?: string
  robQuantity?: string
  minThreshold?: string
  maxThreshold?: string
}

export const PartModal: FC<PartModalProps> = ({
  visible,
  subcomponentId,
  editing,
  onClose,
  onSubmit,
  context,
}) => {
  const resolveRob = (part?: PartDto): number | undefined => {
    if (!part) return undefined
    const raw = part.rob ?? part.robQuantity ?? (part as any)?.currentQty
    if (raw === null || raw === undefined || raw === '') return undefined
    const parsed = Number(raw)
    return Number.isFinite(parsed) ? parsed : undefined
  }

  const initialRob = resolveRob(editing)

  const [formData, setFormData] = useState<PartFormState>({
    id: editing?.id,
    subcomponentId,
    name: editing?.name || '',
    code: editing?.code || '',
    itemCode: editing?.itemCode || editing?.code || '',
    itemShortDescription: editing?.itemShortDescription || '',
    itemLongDescription: editing?.itemLongDescription || '',
    drawNo: editing?.drawNo || '',
    drawingPositionNo: editing?.drawingPositionNo || '',
    location: editing?.location || '',
    uomId: editing?.uom?.id ? String(editing.uom.id) : '',
    unitOfMeasure: editing?.unitOfMeasure || editing?.uom?.code || '',
    robQuantity: editing?.robQuantity != null ? String(editing.robQuantity) : '',
    minThreshold: editing?.minThreshold != null ? String(editing.minThreshold) : '',
    maxThreshold: editing?.maxThreshold != null ? String(editing.maxThreshold) : '',
    rob: initialRob,
    isSpare: editing?.isSpare ?? false,
    criticalSpare: editing?.criticalSpare ?? false,
    manufacturer: editing?.manufacturer,
    partNo: editing?.partNo,
    serialNumber: editing?.serialNumber,
    vesselId: context?.vesselId,
  })
  const [robValue, setRobValue] = useState<string>(initialRob?.toString() ?? '0')
  const [errors, setErrors] = useState<{[key: string]: string}>({})
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [selectedSub, setSelectedSub] = useState<string>('')
  const [selectedOptional, setSelectedOptional] = useState<string>('')
  const [storageLocations, setStorageLocations] = useState<PartStorageLocation[]>([])
  const [isLoadingLocations, setIsLoadingLocations] = useState<boolean>(false)
  const [uoms, setUoms] = useState<UomDto[]>([])
  const [isLoadingUoms, setIsLoadingUoms] = useState<boolean>(false)

  const findCategory = (categoryName: string) =>
    storageLocations.find((c) => c.name === categoryName)

  const findSubLocation = (categoryName: string, subName: string): PartStorageSubLocation | null => {
    const category = findCategory(categoryName)
    if (!category?.subLocations) return null
    return category.subLocations.find((sub) => sub.name === subName) ?? null
  }

  const buildLocationValue = (category: string, sub: string, optional: string) => {
    if (!category) return ''
    if (!sub) return category
    const base = `${category}: ${sub}`
    if (optional) {
      return `${base}: ${optional}`
    }
    return base
  }

  const parseLocation = (
    value?: string | null,
    locations: PartStorageLocation[] = storageLocations
  ) => {
    if (!value) return {category: '', sub: '', optional: ''}
    const parts = value.split(':')
    if (parts.length < 2) {
      return {category: value.trim(), sub: '', optional: ''}
    }
    const category = parts[0].trim()
    const restParts = parts.slice(1).map((part) => part.trim()).filter(Boolean)
    if (restParts.length == 0) {
      return {category, sub: '', optional: ''}
    }
    const categoryConfig = locations.find((loc) => loc.name === category)
    if (!categoryConfig?.subLocations || categoryConfig.subLocations.length === 0) {
      return {category, sub: '', optional: ''}
    }
    const sub = restParts[0]
    if (restParts.length > 1) {
      return {category, sub, optional: restParts.slice(1).join(': ')}
    }
    for (const subLoc of categoryConfig.subLocations) {
      if (sub == subLoc.name) {
        return {category, sub, optional: ''}
      }
      if (sub.startsWith(`${subLoc.name} `)) {
        const optional = sub.substring(subLoc.name.length + 1).trim()
        return {category, sub: subLoc.name, optional}
      }
    }
    return {category, sub, optional: ''}
  }

  const loadStorageLocations = async () => {
    setIsLoadingLocations(true)
    try {
      const data = await getPartStorageLocations()
      setStorageLocations(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Error loading part storage locations:', error)
      setStorageLocations([])
    } finally {
      setIsLoadingLocations(false)
    }
  }

  const loadUoms = async () => {
    setIsLoadingUoms(true)
    try {
      const data = await getUnitsOfMeasure()
      setUoms(buildUomOptions(Array.isArray(data) ? data : []))
    } catch (error) {
      console.error('Error loading units of measure:', error)
      setUoms(buildUomOptions([]))
    } finally {
      setIsLoadingUoms(false)
    }
  }

  useEffect(() => {
    if (visible) {
      loadStorageLocations()
      loadUoms()
    }
  }, [visible])

  useEffect(() => {
    if (editing) {
      const resolvedUomId =
        editing.uom?.id != null
          ? String(editing.uom.id)
          : resolveUomId(editing.unitOfMeasure || editing.uom?.code, uoms)
      const robToUse = resolveRob(editing)
      setFormData({
        id: editing.id,
        subcomponentId: editing.subcomponentId || subcomponentId,
        itemCode: editing.itemCode || editing.code || '',
        itemShortDescription: editing.itemShortDescription || '',
        itemLongDescription: editing.itemLongDescription || '',
        drawNo: editing.drawNo || '',
        drawingPositionNo: editing.drawingPositionNo || '',
        location: editing.location || '',
        uomId: resolvedUomId,
        unitOfMeasure: editing.unitOfMeasure || editing.uom?.code || '',
        robQuantity: editing.robQuantity != null ? String(editing.robQuantity) : '',
        minThreshold: editing.minThreshold != null ? String(editing.minThreshold) : '',
        maxThreshold: editing.maxThreshold != null ? String(editing.maxThreshold) : '',
        name: editing.name || '',
        code: editing.code || '',
        rob: robToUse,
        isSpare: editing?.isSpare ?? false,
        criticalSpare: editing?.criticalSpare ?? false,
        manufacturer: editing?.manufacturer,
        partNo: editing?.partNo,
        serialNumber: editing?.serialNumber,
        vesselId: context?.vesselId,
      })
      setRobValue(robToUse?.toString() ?? '0')
    } else {
      setFormData({
        subcomponentId: subcomponentId,
        name: '',
        code: '',
        itemCode: '',
        itemShortDescription: '',
        itemLongDescription: '',
        drawNo: '',
        drawingPositionNo: '',
        location: '',
        uomId: '',
        unitOfMeasure: '',
        robQuantity: '',
        minThreshold: '',
        maxThreshold: '',
        rob: undefined,
        isSpare: false,
        criticalSpare: false,
        manufacturer: '',
        partNo: '',
        serialNumber: '',
        vesselId: context?.vesselId,
      })
    }
    setErrors({})
    const parsed = parseLocation(editing?.location, storageLocations)
    setSelectedCategory(parsed.category)
    setSelectedSub(parsed.sub)
    setSelectedOptional(parsed.optional)
  }, [editing, subcomponentId, storageLocations, visible, uoms, context?.vesselId])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const newErrors: {[key: string]: string} = {}
    const effectiveName =
      formData.name?.trim() ||
      formData.itemShortDescription?.trim() ||
      formData.itemLongDescription?.trim()
    if (!effectiveName) {
      newErrors.name = 'Name is required'
    }
    if (selectedCategory) {
      const categoryConfig = findCategory(selectedCategory)
      const subLocations = categoryConfig?.subLocations || []
      const allSubOptional = subLocations.length > 0 && subLocations.every((sub) => sub.optional)
      if (subLocations.length > 0 && !allSubOptional && !selectedSub) {
        newErrors.location = 'Sub storage location is required'
      }
      if (selectedSub) {
        const subConfig = findSubLocation(selectedCategory, selectedSub)
        const segments = subConfig?.segments || []
        const allSegOptional = segments.length > 0 && segments.every((seg) => seg.optional)
        if (segments.length > 0 && !allSegOptional && !selectedOptional) {
          newErrors.location = 'Storage segment is required'
        }
      }
    }
    const hasUom = Boolean(formData.uomId || formData.unitOfMeasure?.trim())
    const hasRob = Boolean(formData.robQuantity && formData.robQuantity.trim().length > 0)
    const hasMin = Boolean(formData.minThreshold && formData.minThreshold.trim().length > 0)
    const hasMax = Boolean(formData.maxThreshold && formData.maxThreshold.trim().length > 0)
    if ((hasUom || hasRob || hasMin || hasMax) && !(hasUom && hasRob && hasMin)) {
      newErrors.inventory = 'ROB Quantity, Unit of Measure, and Min Threshold must all be provided together.'
    }
    setErrors(newErrors)
    if (Object.keys(newErrors).length === 0) {
      const finalItemCode = formData.itemCode?.trim()
      const finalCode = formData.code?.trim() || finalItemCode || formData.code
      const resolvedUom = formData.uomId
        ? uoms.find((u) => String(u.id) === formData.uomId)
        : undefined
      const resolvedUomId = resolvedUom && resolvedUom.id > 0 ? resolvedUom.id : undefined
      const robQuantity =
        formData.robQuantity && formData.robQuantity.trim().length > 0
          ? Number(formData.robQuantity)
          : undefined
      const minThreshold =
        formData.minThreshold && formData.minThreshold.trim().length > 0
          ? Number(formData.minThreshold)
          : undefined
      const maxThreshold =
        formData.maxThreshold && formData.maxThreshold.trim().length > 0
          ? Number(formData.maxThreshold)
          : undefined
      const derivedRob = formData.rob != null ? formData.rob : robQuantity
      onSubmit({
        ...formData,
        name: effectiveName || formData.name,
        code: finalCode,
        itemCode: finalItemCode || formData.itemCode,
        itemShortDescription: effectiveName || formData.name,
        uom: resolvedUomId ? {id: resolvedUomId} : formData.uom,
        uomId: resolvedUomId,
        unitOfMeasure: formData.unitOfMeasure || resolvedUom?.code,
        robQuantity,
        minThreshold,
        maxThreshold,
        manufacturer: formData.manufacturer || undefined,
        serialNumber: formData.serialNumber || undefined,
        partNo: formData.partNo || undefined,
        isSpare: formData.isSpare ?? false,
        criticalSpare: formData.criticalSpare ?? false,
        rob: derivedRob,
        location: formData.location,
        equipmentId: context?.equipmentId,
      })
    }
  }

  if (!visible) return null

  return (
    <div style={WRAP_STYLE} tabIndex={-1} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        style={{width: '960px', maxWidth: '98vw', borderRadius: 18}}
        onClick={(e) => e.stopPropagation()}
      >
        <div className='d-flex align-items-center justify-content-between border-bottom px-4 py-3 flex-shrink-0' style={{background: '#F8FAFC'}}>
          <h5 className='modal-title text-dark m-0'>{editing ? 'Edit Part' : 'Add Part'}</h5>
          <button type='button' className='btn-close' onClick={onClose}></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div
            className='flex-grow-1 overflow-auto px-4 py-3 m-6'
            style={{maxHeight: '70vh'}}
          >
            <div className='mb-5'>
              <label className={LABEL}>
                Part Name <span className='text-danger'>*</span>
              </label>
              <input
                type='text'
                className={`form-control text-dark ${errors.name ? 'is-invalid' : ''}`}
                value={formData.name}
                onChange={(e) => {
                  setFormData({...formData, name: e.target.value})
                  if (errors.name) setErrors({...errors, name: ''})
                }}
                placeholder='e.g., Fuel Injector'
              />
              {errors.name && <div className='invalid-feedback'>{errors.name}</div>}
            </div>
            <div className='mb-5 d-flex flex-wrap gap-4'>
              <div className='form-check form-switch'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  id='part-critical-spare'
                  checked={!!formData.criticalSpare}
                  onChange={(e) => setFormData({...formData, criticalSpare: e.target.checked})}
                />
                <label className='form-check-label fw-semibold text-muted' htmlFor='part-critical-spare'>
                  Critical Spare
                </label>
              </div>
            </div>
            <div className='mb-5'>
              <label className={LABEL}>Item Code (Part Code)</label>
              <input
                type='text'
                className='form-control text-dark'
                value={formData.itemCode || ''}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 50)
                  setFormData({...formData, itemCode: value, code: value})
                }}
                placeholder='Item code'
                maxLength={50}
              />
              <div className='form-text text-muted'>Max 50 characters</div>
            </div>
            <div className='mb-5'>
              <label className={LABEL}>Item Long Description</label>
              <textarea
                className='form-control text-dark'
                rows={3}
                value={formData.itemLongDescription || ''}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 2000)
                  setFormData({...formData, itemLongDescription: value})
                }}
                placeholder='Long description'
              />
              <div className='form-text text-muted'>Max 2000 characters</div>
            </div>
            <div className='mb-5'>
              <label className={LABEL}>Draw No</label>
              <input
                type='text'
                className='form-control text-dark'
                value={formData.drawNo || ''}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 100)
                  setFormData({...formData, drawNo: value})
                }}
                placeholder='Draw number'
                maxLength={100}
              />
              <div className='form-text text-muted'>Max 100 characters</div>
            </div>
            <div className='mb-5'>
              <label className={LABEL}>Drawing Position No</label>
              <input
                type='text'
                className='form-control text-dark'
                value={formData.drawingPositionNo || ''}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 50)
                  setFormData({...formData, drawingPositionNo: value})
                }}
                placeholder='Drawing position number'
                maxLength={50}
              />
              <div className='form-text text-muted'>Max 50 characters</div>
            </div>
            {/* <div className='mb-5'>
              <label className={LABEL}>Manufacturer</label>
              <input
                type='text'
                className='form-control text-dark'
                value={formData.manufacturer || ''}
                onChange={(e) => setFormData({...formData, manufacturer: e.target.value})}
                placeholder='Manufacturer'
              />
            </div> */}
            <div className='mb-5'>
              <label className={LABEL}>Storage Location</label>
              <div className='border rounded-2 p-3 bg-light'>
                <div className='row g-3'>
                  <div className='col-12'>
                    <SearchableSelect
                      className='form-select text-dark'
                      value={selectedCategory}
                      onChange={(e) => {
                        const nextCategory = e.target.value
                        setSelectedCategory(nextCategory)
                        setSelectedSub('')
                        setSelectedOptional('')
                        const categoryConfig = findCategory(nextCategory)
                        const hasSub = categoryConfig?.subLocations && categoryConfig.subLocations.length > 0
                        const allSubOptional = hasSub
                          ? categoryConfig!.subLocations!.every((sub) => sub.optional)
                          : false
                        setFormData({
                          ...formData,
                          location: hasSub && !allSubOptional ? '' : buildLocationValue(nextCategory, '', ''),
                        })
                        if (errors.location) {
                          setErrors(prev => ({...prev, location: ''}))
                        }
                      }}
                    >
                      <option value=''>Select storage category</option>
                      {storageLocations.map((loc) => (
                        <option key={loc.name} value={loc.name}>
                          {loc.name}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>
                  {selectedCategory && (findCategory(selectedCategory)?.subLocations?.length || 0) > 0 && (
                    <div className='col-12'>
                      <SearchableSelect
                        className='form-select text-dark'
                        value={selectedSub}
                        onChange={(e) => {
                          const nextSub = e.target.value
                          setSelectedSub(nextSub)
                          setSelectedOptional('')
                          const subConfig = findSubLocation(selectedCategory, nextSub)
                          const segments = subConfig?.segments || []
                          const allSegOptional = segments.length > 0 && segments.every((seg) => seg.optional)
                          setFormData({
                            ...formData,
                            location: segments.length > 0 && !allSegOptional
                              ? ''
                              : buildLocationValue(selectedCategory, nextSub, ''),
                          })
                          if (errors.location) {
                            setErrors(prev => ({...prev, location: ''}))
                          }
                        }}
                      >
                        <option value=''>Select sub location</option>
                        {findCategory(selectedCategory)?.subLocations?.map((option) => (
                          <option key={option.name} value={option.name}>
                            {option.name}
                          </option>
                        ))}
                      </SearchableSelect>
                    </div>
                  )}
                  {selectedCategory && selectedSub && (() => {
                    const subConfig = findSubLocation(selectedCategory, selectedSub)
                    if (!subConfig || !subConfig.segments || subConfig.segments.length === 0) return null
                    return (
                      <div className='col-12'>
                        <SearchableSelect
                          className='form-select text-dark'
                          value={selectedOptional}
                          onChange={(e) => {
                            const nextOptional = e.target.value
                            setSelectedOptional(nextOptional)
                            setFormData({
                              ...formData,
                              location: buildLocationValue(selectedCategory, selectedSub, nextOptional),
                            })
                            if (errors.location) {
                              setErrors(prev => ({...prev, location: ''}))
                            }
                          }}
                        >
                          <option value=''>Select segment</option>
                          {subConfig.segments.map((opt) => (
                            <option key={opt.name} value={opt.name}>
                              {opt.name}
                            </option>
                          ))}
                        </SearchableSelect>
                      </div>
                    )
                  })()}
                </div>
                {isLoadingLocations && <div className='text-muted fs-7 mt-2'>Loading locations...</div>}
                {errors.location && <div className='text-danger fs-7 mt-2'>{errors.location}</div>}
              </div>
            </div>
            <div className='mb-5'>
              <label className={LABEL}>ROB Quantity</label>
              <input
                type='number'
                min={0}
                className='form-control text-dark'
                value={formData.robQuantity || ''}
                onChange={(e) => {
                  setFormData({...formData, robQuantity: e.target.value})
                  if (errors.inventory) setErrors({...errors, inventory: ''})
                }}
                placeholder='0'
              />
            </div>
            <div className='mb-5'>
              <label className={LABEL}>Unit of Measurement</label>
              <SearchableSelect
                className='form-select text-dark'
                value={formData.uomId || ''}
                onChange={(e) => {
                  const selectedId = e.target.value
                  const selected = uoms.find((u) => String(u.id) === selectedId)
                  setFormData({
                    ...formData,
                    uomId: selectedId,
                    unitOfMeasure: selected?.code || formData.unitOfMeasure,
                  })
                  if (errors.inventory) setErrors({...errors, inventory: ''})
                }}
                disabled={isLoadingUoms}
              >
                <option value=''>Select unit</option>
                {uoms.map((uom) => {
                  const label =
                    uom.code && uom.name && uom.code !== uom.name
                      ? `${uom.code} - ${uom.name}`
                      : uom.code || uom.name || `Unit ${uom.id}`
                  return (
                    <option key={uom.id} value={uom.id}>
                      {label}
                    </option>
                  )
                })}
              </SearchableSelect>
            </div>
            <div className='mb-5'>
              <label className={LABEL}>Min Threshold</label>
              <input
                type='number'
                min={0}
                className='form-control text-dark'
                value={formData.minThreshold || ''}
                onChange={(e) => {
                  setFormData({...formData, minThreshold: e.target.value})
                  if (errors.inventory) setErrors({...errors, inventory: ''})
                }}
                placeholder='0'
              />
            </div>
            <div className='mb-5'>
              <label className={LABEL}>Max Threshold</label>
              <input
                type='number'
                min={0}
                className='form-control text-dark'
                value={formData.maxThreshold || ''}
                onChange={(e) => {
                  setFormData({...formData, maxThreshold: e.target.value})
                  if (errors.inventory) setErrors({...errors, inventory: ''})
                }}
                placeholder='0'
              />
            </div>
            {errors.inventory && <div className='text-danger fs-7 mt-2'>{errors.inventory}</div>}
          </div>

          <div className='border-top bg-white px-4 py-3 d-flex justify-content-end gap-2'>
            <button type='button' className='btn btn-light' onClick={onClose}>
              Cancel
            </button>
            <button type='submit' className='btn btn-primary'>
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default PartModal
